import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { formatCLP } from '../lib/format';
import { calcularFinanzas } from '../lib/finanzas';

function Simulator({ userId }) {
  const [data, setData] = useState({ bolsillos: [], movimientos: [], compromisos: [], abonos: [], abonosMeta: [] });
  const [loading, setLoading] = useState(true);

  const [bolsilloId, setBolsilloId] = useState('');
  const [monto, setMonto] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      const [b, m, c, a, am] = await Promise.all([
        supabase.from('bolsillos').select('*').eq('user_id', userId).order('created_at'),
        supabase.from('bolsillo_movimientos').select('*').eq('user_id', userId),
        supabase.from('compromisos').select('*').eq('user_id', userId),
        supabase.from('abonos').select('*').eq('user_id', userId),
        supabase.from('abonos_meta').select('*').eq('user_id', userId),
      ]);
      setData({
        bolsillos: b.data || [],
        movimientos: m.data || [],
        compromisos: c.data || [],
        abonos: a.data || [],
        abonosMeta: am.data || [],
      });
      if (b.data?.length > 0) setBolsilloId(b.data[0].id);
      setLoading(false);
    };
    load();
  }, [userId]);

  const { bolsillos } = data;
  const fin = calcularFinanzas(data);
  const { totalSaldo, totalDisponible } = fin;

  const handleSimular = (e) => {
    e.preventDefault();
    const gasto = parseFloat(monto);
    if (!gasto || gasto <= 0) return;

    const base = bolsilloId
      ? { tipo: 'bolsillo', bolsillo: bolsillos.find(b => b.id === bolsilloId)?.nombre, saldo: fin.porBolsillo(bolsilloId).saldo, disponible: fin.porBolsillo(bolsilloId).disponible }
      : { tipo: 'total', saldo: totalSaldo, disponible: totalDisponible };

    setResultado({
      tipo: base.tipo,
      bolsillo: base.bolsillo,
      gasto,
      saldoAntes: base.saldo,
      saldoTras: base.saldo - gasto,
      disponibleAntes: base.disponible,
      disponibleTras: base.disponible - gasto,
      alcanza: base.disponible >= gasto,
      saldoSuficiente: base.saldo >= gasto,
    });
  };

  const handleReset = () => { setMonto(''); setDescripcion(''); setResultado(null); };

  if (loading) return (
    <div className="bolsillos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
  );

  if (bolsillos.length === 0) return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)', background: 'white', borderRadius: 12 }}>
      <div style={{ fontSize: '3rem', marginBottom: 16 }}>🔮</div>
      <p style={{ fontWeight: 600 }}>Sin categorías registradas</p>
      <p style={{ fontSize: '0.9rem' }}>Crea categorías en "Mis Categorías" para poder simular gastos.</p>
    </div>
  );

  return (
    <div className="deudas-page">
      <div className="page-header">
        <div>
          <h2>Simulador de Gastos</h2>
          <p className="page-subtitle">¿Puedo hacer este gasto? Simula antes de gastar</p>
        </div>
      </div>

      {/* Resumen actual */}
      <div className="summary-cards" style={{ marginBottom: 24 }}>
        <div className="card">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <h4>Saldo total</h4>
            <p className="card-amount neutral">{formatCLP(totalSaldo)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-icon">{totalDisponible >= 0 ? '✅' : '⚠️'}</div>
          <div className="card-content">
            <h4>Disponible real</h4>
            <p className={`card-amount ${totalDisponible >= 0 ? 'positive' : 'negative'}`}>{formatCLP(totalDisponible)}</p>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Después de compromisos y metas</span>
          </div>
        </div>
      </div>

      {/* Estado por categoría */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Disponible por categoría</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {bolsillos.filter(b => !b.aparte).map(b => {
            const { saldo, disponible } = fin.porBolsillo(b.id);
            return (
              <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: '0.92rem' }}>{b.nombre}</span>
                <div style={{ display: 'flex', gap: 16, fontSize: '0.85rem' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Saldo: <strong>{formatCLP(saldo)}</strong></span>
                  <span className={disponible >= 0 ? 'text-success' : 'text-danger'} style={{ fontWeight: 700 }}>
                    Disponible: {formatCLP(disponible)}
                  </span>
                </div>
              </div>
            );
          })}
          {fin.enMetasSinCategoria > 0 && (
            <p className="field-hint" style={{ margin: 0 }}>
              Hay {formatCLP(fin.enMetasSinCategoria)} reservados en metas sin categoría asignada; se descuentan solo del total.
            </p>
          )}
        </div>
      </div>

      {/* Simulador */}
      <div className="card">
        <h3 style={{ fontSize: '1rem', marginBottom: 16 }}>Simular un gasto</h3>
        <form onSubmit={handleSimular}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div className="form-group">
              <label>Monto a gastar</label>
              <input type="number" value={monto} onChange={e => { setMonto(e.target.value); setResultado(null); }}
                placeholder="Ej: 15000" min="1" required />
            </div>
            <div className="form-group">
              <label>¿En qué categoría?</label>
              <select value={bolsilloId} onChange={e => { setBolsilloId(e.target.value); setResultado(null); }}>
                <option value="">— Ver impacto total —</option>
                {bolsillos.filter(b => !b.aparte).map(b => <option key={b.id} value={b.id}>{b.nombre} ({formatCLP(fin.porBolsillo(b.id).disponible)})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Descripción (opcional)</label>
              <input type="text" value={descripcion} onChange={e => setDescripcion(e.target.value)}
                placeholder="Ej: Zapatos, cena..." />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" className="btn-primary">🔮 Simular</button>
            {resultado && <button type="button" className="btn-secondary" onClick={handleReset}>Limpiar</button>}
          </div>
        </form>

        {resultado && (
          <div style={{ marginTop: 20, padding: '16px 18px', borderRadius: 12, background: resultado.alcanza ? '#f0fdf4' : '#fef2f2', border: `1px solid ${resultado.alcanza ? '#86efac' : '#fca5a5'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: '2rem' }}>{resultado.alcanza ? '✅' : '❌'}</span>
              <div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: '1.05rem', color: resultado.alcanza ? '#15803d' : '#b91c1c' }}>
                  {resultado.alcanza
                    ? '¡Sí puedes! Tienes saldo disponible para este gasto.'
                    : `No alcanza. Te faltarían ${formatCLP(Math.abs(resultado.disponibleTras))} en disponible.`
                  }
                </p>
                {descripcion && <p style={{ margin: '2px 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Para: {descripcion}</p>}
              </div>
            </div>
            <div className="sim-resultado-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, fontSize: '0.88rem' }}>
              <div style={{ background: 'white', borderRadius: 8, padding: '10px 12px' }}>
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  {resultado.tipo === 'bolsillo' ? `Saldo en ${resultado.bolsillo}` : 'Saldo total'} antes
                </p>
                <p style={{ margin: 0, fontWeight: 700 }}>{formatCLP(resultado.saldoAntes)}</p>
              </div>
              <div style={{ background: 'white', borderRadius: 8, padding: '10px 12px' }}>
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.75rem' }}>Gasto simulado</p>
                <p style={{ margin: 0, fontWeight: 700, color: '#ef4444' }}>−{formatCLP(resultado.gasto)}</p>
              </div>
              <div style={{ background: 'white', borderRadius: 8, padding: '10px 12px' }}>
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.75rem' }}>Disponible después</p>
                <p style={{ margin: 0, fontWeight: 700, color: resultado.disponibleTras >= 0 ? '#16a34a' : '#b91c1c' }}>
                  {formatCLP(resultado.disponibleTras)}
                </p>
              </div>
              {!resultado.saldoSuficiente && (
                <div style={{ background: '#fef9c3', borderRadius: 8, padding: '10px 12px', gridColumn: '1 / -1' }}>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: '#854d0e' }}>
                    ⚠ El saldo bruto tampoco alcanza — necesitarías ingresar {formatCLP(Math.abs(resultado.saldoTras))} más.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Simulator;
